import React, { ChangeEvent, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Body2, Heading3 } from '@/components/ui/typography'
import { Owner } from 'api/Clients/Types/ClientDetailedDto'
import { ReactDatePicker } from 'components/DatePicker/DatePicker'
import { CommonReactDatePickerWrapper } from 'components/Form/Form.styles'
import { Option, Select } from 'components/Select'
import { Info } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { isEmpty } from 'utils/is-empty'

import styles from './ClientDetailsCorporateForm.module.scss'

interface OuterProps {
  data: Owner

  onChange(values: Partial<Owner>): void
}

export const OwnerForm: React.FC<OuterProps> = ({ onChange, data }) => {
  const [form, setForm] = useState<Partial<Owner>>({
    id: data?.id,
    name: data.name,
    ownership: data.ownership,
    birthday: data.birthday,
    street: data.street,
    city: data.city,
    state: data.state,
    zipCode: data.zipCode,
    country: data.country,
    taxIdentificationNumber: data.taxIdentificationNumber,
  })
  const [error, setErrors] = useState<string>()

  const identityApiClient = useApiClient(IdentityApiClient)

  const countries = useAppSelector((state) => state.identity.countries)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, [dispatch, countries]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [event.target.name]: event.target.value })
  }

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [event.target.name]: Number.parseInt(event.target.value, 10) })
  }

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setForm({ ...form, birthday: date.toISOString() })
    }
  }

  const handleCountryChange = (value: string) => {
    const country = (countries || []).find((x) => x.id === value)
    if (country) setForm({ ...form, country })
  }

  const handleSubmitForm = () => {
    validateForm()

    if (isValidForm()) {
      onChange(form)
    }
  }

  const validateForm = () => {
    if (!isValidForm()) {
      setErrors('Please fill required fields!')
    } else {
      setErrors(undefined)
    }
  }

  const isValidForm = () => {
    return Object.values(form).every((value) => {
      return !!value
    })
  }

  return (
    <div className={styles.container}>
      <div>
        <Heading3>Shareholder</Heading3>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Name<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='name' value={form.name} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          DOB<span>*</span>
        </Body2>
        <div className={styles.input}>
          <CommonReactDatePickerWrapper>
            <ReactDatePicker
              selected={form.birthday ? new Date(form.birthday) : null}
              dropdownMode='select'
              dateFormat='dd/MM/yyyy'
              onChange={handleDateChange}
              isClearable
              showYearDropdown
              wrapperClassName='react-date-picker'
              customInput={
                <input name='birthday' type='text' autoComplete='off' style={{ width: '100%' }} />
              }
            />
          </CommonReactDatePickerWrapper>
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Country of Residence<span>*</span>
        </Body2>
        <div className={styles.input}>
          <Select
            placeholder='Country of Residence'
            value={form.country?.id}
            allowClear
            showSearch
            fullWidth
            onChange={handleCountryChange}
          >
            {countries.map((country) => (
              <Option value={country.id} key={country.id}>
                {country.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Percantage<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input
            name='ownership'
            value={form.ownership}
            type='number'
            min={0}
            max={100}
            required
            onChange={handleNumberChange}
          />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Street Name, Street Number, Apt. Number<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='street' value={form?.street} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          City<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='city' value={form?.city} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          State/Region<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='state' value={form?.state} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          ZIP/Postal Code<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input
            name='zipCode'
            value={form?.zipCode}
            type='text'
            required
            onChange={handleChange}
          />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold className='flex items-center gap-1'>
          TIN / GSTN<span>*</span>
          <Tooltip key='right'>
            <TooltipTrigger>
              <Info size={14} />
            </TooltipTrigger>
            <TooltipContent side='right'>
              <span>Tax Identification Number / Goods Services Tax Number</span>
            </TooltipContent>
          </Tooltip>
        </Body2>
        <div className={styles.input}>
          <input
            name='taxIdentificationNumber'
            value={form?.taxIdentificationNumber}
            type='text'
            required
            onChange={handleChange}
          />
        </div>
      </div>

      <div className={styles.submit}>
        <Button type='button' onClick={handleSubmitForm}>
          Save
        </Button>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  )
}
